import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'

import { Download, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react'

import { currencyFormatter } from '../../library/general'

import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert } from '@/components/ui/alert'
import { Spinner } from '@/components/ui/spinner'
import { DataTable } from '@/components/ui/data-table'

import {
  BILLING_DATE_COLUMN,
  DUE_DATE_COLUMN,
  NAME_COLUMN,
  TOTAL_COLUMN,
  BALANCE_COLUMN,
  RECEIVED_AMOUNT_COLUMN,
  CREDIT_APPLIED_AMOUNT_COLUMN,
  PAYMENT_TERMS_COLUMN,
  BILLING_DATE_COLUMN_FRIENDLY,
  DUE_DATE_COLUMN_FRIENDLY,
  NAME_COLUMN_FRIENDLY,
  TOTAL_COLUMN_FRIENDLY,
  BALANCE_COLUMN_FRIENDLY,
  RECEIVED_AMOUNT_COLUMN_FRIENDLY,
  CREDIT_APPLIED_AMOUNT_COLUMN_FRIENDLY,
  PAYMENT_TERMS_COLUMN_FRIENDLY,
  requestInvoices,
  requestSortInvoices,
  requestPageStep,
  requestInvoiceDownload,
} from '../../stores/invoices'

import './index.css'

const fmtDate = val => (val ? new Date(val).toLocaleDateString('en-US', { timeZone: 'UTC' }) : '')

function ViewAllInvoices({
  fetched,
  totalSubsetInvoices,
  totalBalanceDue,
  subsetInvoices,
  loading,
  error,
  hasError,
  sortColumn,
  sortDirection,
  pageNumber,
  numberOfPages,
  requestInvoices,
  requestSortInvoices,
  requestPageStep,
  requestInvoiceDownload,
}) {
  useEffect(() => {
    if (!fetched) requestInvoices()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const sorting = useMemo(
    () => (sortColumn ? [{ id: sortColumn, desc: (sortDirection[sortColumn] || 'desc') === 'desc' }] : []),
    [sortColumn, sortDirection]
  )
  const handleSortingChange = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    if (!next.length) return
    requestSortInvoices(next[0].id, next[0].desc ? 'desc' : 'asc')
  }

  const columns = useMemo(() => {
    const money = key => ({ getValue }) => currencyFormatter.format(getValue())
    return [
      {
        id: '#', header: '#', accessorKey: '#', size: 38,
        enableSorting: false, enableResizing: false,
        meta: { reorderable: false, align: 'right', cellClassName: 'text-muted-foreground' },
      },
      {
        id: NAME_COLUMN, header: NAME_COLUMN_FRIENDLY, accessorKey: NAME_COLUMN, size: 360,
        enableSorting: false,
        meta: { cellClassName: 'whitespace-normal' },
        cell: ({ row }) => (
          <div>
            <div className="mb-0.5 font-semibold">{row.original[NAME_COLUMN]}</div>
            {row.original.invoiceFiles && row.original.invoiceFiles.map(f => (
              <div key={f.id} className="flex items-center gap-1">
                <Download className="h-3.5 w-3.5 text-primary" />
                <button
                  type="button"
                  onClick={() => requestInvoiceDownload(f.id)}
                  className="text-xs text-primary hover:underline"
                >
                  {f.name}.{f.extension}
                </button>
              </div>
            ))}
          </div>
        ),
      },
      { id: BILLING_DATE_COLUMN, header: BILLING_DATE_COLUMN_FRIENDLY, accessorKey: BILLING_DATE_COLUMN, size: 120, cell: ({ getValue }) => fmtDate(getValue()) },
      { id: DUE_DATE_COLUMN, header: DUE_DATE_COLUMN_FRIENDLY, accessorKey: DUE_DATE_COLUMN, size: 120, cell: ({ getValue }) => fmtDate(getValue()) },
      { id: PAYMENT_TERMS_COLUMN, header: PAYMENT_TERMS_COLUMN_FRIENDLY, accessorKey: PAYMENT_TERMS_COLUMN, size: 120, enableSorting: false },
      { id: TOTAL_COLUMN, header: TOTAL_COLUMN_FRIENDLY, accessorKey: TOTAL_COLUMN, size: 110, enableSorting: false, meta: { align: 'right' }, cell: money(TOTAL_COLUMN) },
      {
        id: BALANCE_COLUMN, header: BALANCE_COLUMN_FRIENDLY, accessorKey: BALANCE_COLUMN, size: 110,
        enableSorting: false, meta: { align: 'right' },
        cell: ({ getValue }) => {
          const balance = getValue()
          return balance !== 0
            ? <span className="font-semibold italic text-warning">{currencyFormatter.format(balance)}</span>
            : currencyFormatter.format(balance)
        },
      },
      { id: CREDIT_APPLIED_AMOUNT_COLUMN, header: CREDIT_APPLIED_AMOUNT_COLUMN_FRIENDLY, accessorKey: CREDIT_APPLIED_AMOUNT_COLUMN, size: 110, enableSorting: false, meta: { align: 'right' }, cell: money(CREDIT_APPLIED_AMOUNT_COLUMN) },
      { id: RECEIVED_AMOUNT_COLUMN, header: RECEIVED_AMOUNT_COLUMN_FRIENDLY, accessorKey: RECEIVED_AMOUNT_COLUMN, size: 110, enableSorting: false, meta: { align: 'right' }, cell: money(RECEIVED_AMOUNT_COLUMN) },
    ]
  }, [requestInvoiceDownload])

  return (
    <div className="space-y-3">
      {hasError && <Alert variant="error">{error}</Alert>}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b px-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-3">
              <h2 className="text-sm font-semibold leading-tight">Invoices</h2>
              {totalBalanceDue > 0 && (
                <span className="text-sm font-semibold text-warning">
                  Balance Due: {currencyFormatter.format(totalBalanceDue)}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {`${totalSubsetInvoices} total · Page ${pageNumber}/${Math.max(numberOfPages, 1)}`}
            </p>
          </div>

          <Button variant="outline" size="icon-sm" onClick={requestInvoices} disabled={loading} title="Refresh">
            {loading ? <Spinner size={16} /> : <RefreshCw className="h-4 w-4" />}
          </Button>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon-sm" onClick={() => requestPageStep(pageNumber - 1)} disabled={pageNumber <= 1} title="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input
              type="number"
              value={pageNumber}
              onChange={e => requestPageStep(parseInt(e.target.value) || 1)}
              onFocus={e => e.target.select()}
              min={1}
              max={Math.max(numberOfPages, 1)}
              className="h-8 w-14 text-center"
            />
            <span className="whitespace-nowrap text-sm text-muted-foreground">/ {Math.max(numberOfPages, 1)}</span>
            <Button variant="outline" size="icon-sm" onClick={() => requestPageStep(pageNumber + 1)} disabled={pageNumber >= numberOfPages} title="Next page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DataTable
          tableId="invoices"
          columns={columns}
          data={subsetInvoices}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          enableSortingRemoval={false}
          getRowId={(row, i) => row.id || String(i)}
          isLoading={loading}
          emptyMessage="No invoices found"
        />

        <div className="flex items-center justify-center gap-2 border-t px-3 py-2">
          <Button variant="outline" size="sm" onClick={() => requestPageStep(pageNumber - 1)} disabled={pageNumber <= 1}>
            <ChevronLeft className="h-4 w-4" /> Previous
          </Button>
          <Button variant="outline" size="sm" onClick={() => requestPageStep(pageNumber + 1)} disabled={pageNumber >= numberOfPages}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </Card>
    </div>
  )
}

const mapStateToProps = (state, route) => {
  const {
    fetched,
    invoices,
    totalSubsetInvoices,
    totalBalanceDue,
    subsetInvoices,
    loading,
    error,
    hasError,
    pageNumber,
    pageSize,
    numberOfPages,
    sortColumn,
    sortDirection,
  } = state.stores.invoices

  return {
    ...route.match.params,
    fetched,
    invoices,
    totalSubsetInvoices,
    totalBalanceDue,
    subsetInvoices,
    loading,
    error,
    hasError,
    pageNumber,
    pageSize,
    numberOfPages,
    sortColumn,
    sortDirection,
  }
}

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({
    requestInvoices,
    requestSortInvoices,
    requestPageStep,
    requestInvoiceDownload,
  }, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ViewAllInvoices))
